<template>
  <div class="legal-shell">
    <header class="legal-top no-print">
      <nav class="legal-nav">
        <router-link to="/" class="brand">
          <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy" />
          <span>OilBuddy AI</span>
        </router-link>
        <div class="doc-switch">
          <router-link to="/privacy" :class="{ on: doc.key === 'privacy' }">Privacy Policy</router-link>
          <router-link to="/terms" :class="{ on: doc.key === 'terms' }">Terms of Service</router-link>
          <router-link to="/cookies" :class="{ on: doc.key === 'cookies' }">Cookie Policy</router-link>
          <router-link to="/data-deletion">Data Deletion</router-link>
          <router-link to="/contact" class="contact">Contact</router-link>
        </div>
      </nav>
    </header>

    <div class="legal-body">
      <aside class="toc no-print">
        <p class="toc-kicker">Contents</p>
        <button
          v-for="section in doc.sections"
          :key="section.id"
          type="button"
          class="toc-link"
          @click="scrollTo(section.id)"
        >
          <span>{{ section.number }}</span>{{ section.heading }}
        </button>
      </aside>

      <article class="document">
        <div class="doc-banner">
          <div>
            <p class="kicker">OilBuddy AI — Legal &amp; Compliance</p>
            <h1>{{ doc.title }}</h1>
            <p class="lede">{{ doc.summary }}</p>
          </div>
          <dl class="meta">
            <div><dt>Document</dt><dd>{{ doc.documentId }}</dd></div>
            <div><dt>Revision</dt><dd>{{ doc.revision }}</dd></div>
            <div><dt>Effective</dt><dd>{{ doc.effective }}</dd></div>
            <div><dt>Last updated</dt><dd>{{ doc.updated }}</dd></div>
            <div class="wide"><dt>Classification</dt><dd>{{ doc.classification }}</dd></div>
          </dl>
        </div>

        <p class="notice">
          This document is written for oilfield service companies, operators’ field representatives, independent contractors, employees, and drivers who use OilBuddy to record location work, tickets, tanks, wells, invoices, and payroll. It is a binding policy for use of the Service. It is not a substitute for your master service agreement, operator HSE manual, or advice from your counsel.
        </p>

        <section v-for="section in doc.sections" :id="section.id" :key="section.id" class="article">
          <h2><span>Article {{ section.number }}</span>{{ section.heading }}</h2>
          <p v-for="(para, i) in section.paragraphs || []" :key="'p' + i">{{ para }}</p>
          <ul v-if="section.bullets?.length">
            <li v-for="item in section.bullets" :key="item">{{ item }}</li>
          </ul>
          <p v-for="(para, i) in section.paragraphs2 || []" :key="'t' + i">{{ para }}</p>
          <div v-for="sub in section.subheads || []" :key="sub.heading" class="sub">
            <h3>{{ sub.heading }}</h3>
            <p v-for="(para, i) in sub.paragraphs || []" :key="i">{{ para }}</p>
            <ul v-if="sub.bullets?.length">
              <li v-for="item in sub.bullets" :key="item">{{ item }}</li>
            </ul>
          </div>
        </section>

        <footer class="doc-end">
          <p>
            End of {{ doc.documentId }}. Related:
            <router-link to="/privacy">Privacy Policy</router-link> ·
            <router-link to="/terms">Terms of Service</router-link> ·
            <router-link to="/cookies">Cookie Policy</router-link> ·
            <router-link to="/data-deletion">Data deletion</router-link> ·
            <a href="mailto:contact@oilbuddy.ai">contact@oilbuddy.ai</a>
          </p>
          <p class="copy">© {{ year }} OilBuddy AI. All rights reserved. North Dakota oilfield operations software.</p>
        </footer>
      </article>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { legalDocs, type LegalDoc } from '@/legal/legalDocuments'

const route = useRoute()
const year = new Date().getFullYear()

const doc = computed<LegalDoc>(() => {
  if (route.path.includes('terms')) return legalDocs.terms
  if (route.path.includes('cookie')) return legalDocs.cookies
  return legalDocs.privacy
})

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<style scoped>
.legal-shell {
  min-height: 100vh;
  background: #0b1220;
  color: #e2e8f0;
}
.legal-top {
  border-bottom: 1px solid #1e293b;
  background: #0b1220;
  position: sticky;
  top: 0;
  z-index: 20;
}
.legal-nav {
  max-width: 1180px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-weight: 700;
}
.brand img { width: 36px; height: 36px; border-radius: 10px; }
.doc-switch { display: flex; gap: 16px; flex-wrap: wrap; font-size: 13px; }
.doc-switch a { color: #94a3b8; }
.doc-switch a.on, .doc-switch a:hover { color: #34d399; }
.doc-switch .contact { color: #67e8f9; }
.legal-body {
  max-width: 1180px;
  margin: 0 auto;
  padding: 28px 24px 64px;
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 36px;
  align-items: start;
}
.toc {
  position: sticky;
  top: 84px;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.toc-kicker {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  font-weight: 700;
  margin-bottom: 4px;
}
.toc-link {
  color: #94a3b8;
  line-height: 1.35;
  display: flex;
  gap: 8px;
  background: none;
  border: 0;
  padding: 0;
  text-align: left;
  cursor: pointer;
  font: inherit;
}
.toc-link span { color: #34d399; font-variant-numeric: tabular-nums; min-width: 1.2em; }
.toc-link:hover { color: #fff; }
.document {
  background: #f8fafc;
  color: #0f172a;
  border-radius: 4px;
  padding: 48px 52px 40px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
}
.doc-banner h1 {
  font-size: 34px;
  letter-spacing: -0.02em;
  margin: 6px 0 14px;
  color: #0f172a;
}
.kicker {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #047857;
  font-weight: 700;
}
.lede { color: #334155; line-height: 1.6; margin-bottom: 22px; }
.meta {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 24px;
  border: 1px solid #cbd5e1;
  background: #fff;
  padding: 14px 16px;
  font-size: 12px;
}
.meta dt { color: #64748b; text-transform: uppercase; letter-spacing: 0.06em; font-size: 10px; }
.meta dd { font-weight: 600; color: #0f172a; }
.meta .wide { grid-column: 1 / -1; }
.notice {
  margin: 22px 0 28px;
  padding: 14px 16px;
  background: #ecfdf5;
  border-left: 4px solid #059669;
  color: #064e3b;
  font-size: 14px;
  line-height: 1.55;
}
.article { margin: 0 0 32px; scroll-margin-top: 88px; }
.article h2 {
  font-size: 20px;
  border-bottom: 1px solid #cbd5e1;
  padding-bottom: 8px;
  margin-bottom: 14px;
  color: #0f172a;
}
.article h2 span {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #047857;
  margin-bottom: 4px;
}
.article p, .sub p { color: #1e293b; line-height: 1.7; margin-bottom: 12px; font-size: 15px; }
.article ul { margin: 0 0 14px 1.2rem; color: #1e293b; }
.article li { margin-bottom: 8px; line-height: 1.6; padding-left: 4px; }
.sub { margin: 18px 0; }
.sub h3 { font-size: 15px; color: #0f172a; margin-bottom: 8px; }
.doc-end {
  border-top: 2px solid #0f172a;
  padding-top: 16px;
  font-size: 13px;
  color: #475569;
}
.doc-end a { color: #047857; font-weight: 600; }
.copy { margin-top: 8px; }

@media (max-width: 900px) {
  .legal-body { grid-template-columns: 1fr; }
  .toc { position: static; }
  .document { padding: 28px 20px; }
}

@media print {
  .no-print { display: none !important; }
  .legal-shell { background: #fff; }
  .legal-body { display: block; padding: 0; max-width: none; }
  .document { box-shadow: none; padding: 0; }
}
</style>
